import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "./Sidebar";
import HeaderBar from "./HeaderBar";
import { Drawer, Layout } from "antd";
import { localData } from "@/services/helper";
import { useDispatch, useSelector } from "react-redux";
import { localKeys } from "@/utils/constants";
import { cookiesAction } from "@/actions/cookiesAction";
import { AuthDataType } from "@/types/authTypes";
import KycGuard from "./KycGuard";
import { ExpiryProvider, useExpiryContext } from "@/utils/expiryCheck";
import GracePeriodModal from "./gracePeriodModal";

const { Content } = Layout;

type StoredCookieConsent = {
    sessionId?: string;
    consent?: {
        essential?: boolean;
        analytics?: boolean;
    };
    choice?: string;
    timestamp?: string;
    userId?: string;
};

type StoredAuthData = Pick<AuthDataType, "id" | "token">;

const GracePeriodModalWrapper = () => {
    const { showGracePeriodModal, closeGracePeriodModal } = useExpiryContext();
    return <GracePeriodModal open={showGracePeriodModal} onClose={closeGracePeriodModal} />;
};

const MainLayout: React.FC<{ children?: React.ReactNode; noFooter?: boolean }> = ({
    children,
    noFooter = true,
}) => {
    const dispatch = useDispatch();

    const cookies = useMemo<StoredCookieConsent | null>(
        () => localData.get<StoredCookieConsent>(localKeys.defaultStorageKey),
        [],
    );
    const authData = useMemo<StoredAuthData | null>(
        () => localData.get<StoredAuthData>(localKeys.authData),
        [],
    );
    const getCookiesResponse = useSelector((state: any) => state.getCookiesReducer || {});
    const updateCookiesResponse = useSelector((state: any) => state.updateCookiesReducer || {});
    const headerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const { showGracePeriodModal, closeGracePeriodModal } = useExpiryContext();
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try {
            const v = localData.get("sidebarCollapsed");
            if (v !== null) return v === "true";
        } catch (e) {}
        if (typeof window !== "undefined") return window.innerWidth < 768;
        return false;
    });
    const [enableDrawer, setEnableDrawer] = useState<boolean>(false);
    const [height, setHeight] = useState<number>(0);

    useEffect(() => {
        try {
            localData.set("sidebarCollapsed", collapsed ? "true" : "false");
        } catch (e) {}
    }, [collapsed]);

    useEffect(() => {
        if (cookies?.userId) return;
        dispatch(cookiesAction.getCookies(cookies?.sessionId));
    }, [dispatch, cookies?.sessionId]);

    useEffect(() => {
        if (!getCookiesResponse.isSuccess) return;
        if (!cookies?.sessionId) return;
        if (!authData?.id) return;
        const updateData = {
            id: cookies.sessionId,
            body: {
                userId: authData.id,
                ...(cookies?.consent?.analytics
                    ? { consent: { analytics: cookies?.consent.analytics } }
                    : null),
            },
        };
        dispatch(cookiesAction.updateCookies(updateData));
    }, [dispatch, getCookiesResponse.isSuccess, cookies?.sessionId, authData?.id]);

    useEffect(() => {
        if (updateCookiesResponse.isSuccess) {
            localData.set(localKeys.defaultStorageKey, {
                ...cookies,
                userId: authData?.id,
            });
        }
    }, [updateCookiesResponse]);

    useEffect(() => {
        const onResize = () => {
            try {
                const width = window.innerWidth;
                setHeight(window.innerHeight);
                if (width < 768 && !collapsed) setCollapsed(true);
                const saved = localData.get("sidebarCollapsed");
                if (width >= 992 && collapsed && saved === "false") {
                    setCollapsed(false);
                }
                if (width < 568) {
                    setEnableDrawer(true);
                    setCollapsed(false);
                }
                if (width > 568) {
                    setEnableDrawer(false);
                }
            } catch (e) {}
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [collapsed]);
    useEffect(() => {
        setHeight(window.innerHeight);
    }, []);

    useEffect(() => {
        if (headerRef.current === null && contentRef.current === null) return;
        setHeight((contentRef.current?.offsetHeight ?? 0) - (headerRef.current?.offsetHeight ?? 0));
    }, [headerRef.current?.offsetHeight]);

    return (
        <ExpiryProvider>
            <Layout className="app-layout">
                {enableDrawer ? (
                    <Drawer
                        open={collapsed}
                        placement="left"
                        headerStyle={{ display: "none" }}
                        width={"initial"}
                        className="app-drawer"
                        onClose={() => setCollapsed(false)}
                    >
                        <div onClick={() => setCollapsed(false)}>
                            <Sidebar collapsed={false} />
                        </div>
                    </Drawer>
                ) : (
                    <Sidebar collapsed={collapsed} />
                )}
                <Layout className="app-shell" ref={contentRef}>
                    <HeaderBar
                        onToggle={() => setCollapsed((c) => !c)}
                        collapsed={collapsed}
                        ref={headerRef}
                    />
                    <Content className="app-content">
                        <main className="app-main">
                            <GracePeriodModalWrapper />
                            <KycGuard>{children}</KycGuard>
                        </main>
                    </Content>
                    {/* {!noFooter && (
                    <Footer style={{
                    width: "100%",
                    padding: 12,
                    borderTop: "1px solid #f0f0f0",
                    color: "#000",
                    fontSize: 12,
                    textAlign: "center",
                    backgroundColor:"white"
                }}>
                        © <span>P<span style={{color:"#4338ca"}}>4</span>P</span>
                    </Footer>
                )} */}
                </Layout>
            </Layout>
        </ExpiryProvider>
    );
};

export default MainLayout;
